(() => {
  'use strict';

  const VERSION = 4;
  if (window.WUBDelivery?.version === VERSION) {
    window.WUBDelivery.refreshConfig?.();
    window.WUBDelivery.syncAll?.();
    return;
  }

  const I18N = window.WUB?.i18n || {};
  const DELIVERY_I18N = I18N.delivery || {};
  const STOCK_I18N = I18N.stock || {};
  const LOCALE = I18N.locale || document.documentElement.lang || 'en';
  const interpolate = window.WUB?.interpolate || ((template, values = {}) => String(template || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match));

  const TIME_ZONE = 'Europe/Amsterdam';
  const WEEKDAYS = Object.freeze({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 });
  const HOLIDAY_CACHE = new Map();
  const MAX_LOOKAHEAD_DAYS = 370;
  const FALLBACK_CONFIG = Object.freeze({
    enabled: true,
    standardCutoff: { hour: 19, minute: 0 },
    shippingRules: { 0: 'closed', 1: 'standard', 2: 'standard', 3: 'standard', 4: 'standard', 5: 'standard', 6: 'closed' },
    deliveryDays: { 0: false, 1: true, 2: true, 3: true, 4: true, 5: true, 6: true },
    holidays: {},
    exceptions: ''
  });

  function readConfig() {
    const node = document.getElementById('WubDeliveryConfig');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('WUB delivery: invalid config JSON', error);
      return null;
    }
  }

  function normalizeConfig(input = {}) {
    return {
      enabled: input.enabled !== false,
      standardCutoff: { ...FALLBACK_CONFIG.standardCutoff, ...(input.standardCutoff || {}) },
      shippingRules: { ...FALLBACK_CONFIG.shippingRules, ...(input.shippingRules || {}) },
      deliveryDays: { ...FALLBACK_CONFIG.deliveryDays, ...(input.deliveryDays || {}) },
      holidays: { ...FALLBACK_CONFIG.holidays, ...(input.holidays || {}) },
      exceptions: typeof input.exceptions === 'string' ? input.exceptions : ''
    };
  }

  let config = normalizeConfig(readConfig() || FALLBACK_CONFIG);
  let exceptions = new Map();

  const utcDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day));
  const addDays = (date, days) => {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  };
  const dateKey = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  function zonedNow(date = new Date()) {
    try {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
      const parts = {};
      formatter.formatToParts(date).forEach(({ type, value }) => {
        if (type !== 'literal') parts[type] = value;
      });
      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        weekday: WEEKDAYS[parts.weekday] ?? 0,
        hour: Number(parts.hour || 0),
        minute: Number(parts.minute || 0),
        second: Number(parts.second || 0)
      };
    } catch (error) {
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        weekday: date.getDay(),
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds()
      };
    }
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    return utcDate(year, month, ((h + l - 7 * m + 114) % 31) + 1);
  }

  function holidayMap(year) {
    if (HOLIDAY_CACHE.has(year)) return HOLIDAY_CACHE.get(year);
    const map = new Map();
    const add = (date, key) => map.set(dateKey(date), key);
    const easter = easterSunday(year);

    add(utcDate(year, 1, 1), 'new_year');
    add(addDays(easter, -2), 'good_friday');
    add(easter, 'easter_sun');
    add(addDays(easter, 1), 'easter_mon');

    let kingsDay = utcDate(year, 4, 27);
    if (kingsDay.getUTCDay() === 0) kingsDay = utcDate(year, 4, 26);
    add(kingsDay, 'kings_day');

    add(utcDate(year, 5, 5), 'liberation');
    add(addDays(easter, 39), 'ascension');
    add(addDays(easter, 49), 'pentecost_sun');
    add(addDays(easter, 50), 'pentecost_mon');
    add(utcDate(year, 12, 25), 'christmas_1');
    add(utcDate(year, 12, 26), 'christmas_2');

    HOLIDAY_CACHE.set(year, map);
    return map;
  }

  function standardCutoff() {
    return {
      hour: Math.max(0, Math.min(23, Number(config.standardCutoff?.hour ?? 19))),
      minute: Math.max(0, Math.min(59, Number(config.standardCutoff?.minute ?? 0)))
    };
  }

  function cutoffFromToken(token) {
    const standard = standardCutoff();
    if (!token || token === 'standard') return standard;
    const match = String(token).match(/(\d{1,2}):(\d{2})$/);
    if (!match) return standard;
    return {
      hour: Math.max(0, Math.min(23, Number(match[1]))),
      minute: Math.max(0, Math.min(59, Number(match[2])))
    };
  }

  function weekdayRule(token) {
    if (!token || token === 'closed') {
      return { ship: false, delivery: null, cutoff: standardCutoff() };
    }
    return { ship: true, delivery: null, cutoff: cutoffFromToken(token) };
  }

  function holidayRule(token) {
    const value = String(token || 'closed');
    if (value === 'closed') {
      return { ship: false, delivery: false, cutoff: standardCutoff() };
    }

    const shipOnly = value.startsWith('ship_only_');
    const open = value.startsWith('open_');
    if (!shipOnly && !open) {
      return { ship: false, delivery: false, cutoff: standardCutoff() };
    }

    const cutoffToken = value.replace(/^ship_only_/, '').replace(/^open_/, '');
    return {
      ship: true,
      delivery: shipOnly ? false : true,
      cutoff: cutoffFromToken(cutoffToken)
    };
  }

  function exceptionMap() {
    const map = new Map();
    String(config.exceptions || '').split(/\r?\n/).forEach((line) => {
      const clean = line.trim();
      if (!clean || clean.startsWith('#')) return;

      const [date, modeRaw, cutoffRaw] = clean.split('|').map((part) => String(part || '').trim());
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;

      const mode = (modeRaw || 'closed').toLowerCase();
      if (mode === 'closed') {
        map.set(date, { ship: false, delivery: false, cutoff: standardCutoff(), source: 'exception' });
      } else if (mode === 'open' || mode === 'ship_only') {
        map.set(date, {
          ship: true,
          delivery: mode === 'open',
          cutoff: cutoffFromToken(cutoffRaw || 'standard'),
          source: 'exception'
        });
      }
    });
    return map;
  }

  function refreshConfig() {
    const next = readConfig();
    if (next) config = normalizeConfig(next);
    exceptions = exceptionMap();
    return config;
  }

  refreshConfig();

  function ruleForDate(date) {
    const key = dateKey(date);
    const exception = exceptions.get(key);
    if (exception) return exception;

    const holidayKey = holidayMap(date.getUTCFullYear()).get(key);
    if (holidayKey) {
      return { ...holidayRule(config.holidays?.[holidayKey]), source: `holiday:${holidayKey}` };
    }

    const weekday = date.getUTCDay();
    return { ...weekdayRule(config.shippingRules?.[weekday]), source: `weekday:${weekday}` };
  }

  const carrierDelivers = (date) => config.deliveryDays?.[date.getUTCDay()] === true;
  const isShipDay = (date) => config.enabled !== false && ruleForDate(date).ship === true;

  function isDeliveryDay(date) {
    if (config.enabled === false) return false;
    if (ruleForDate(date).delivery === false) return false;
    return carrierDelivers(date);
  }

  function nextDate(fromDate, predicate) {
    let date = addDays(fromDate, 1);
    for (let i = 0; i < MAX_LOOKAHEAD_DAYS; i += 1) {
      if (predicate(date)) return date;
      date = addDays(date, 1);
    }
    return null;
  }

  const nextShipDate = (fromDate) => nextDate(fromDate, isShipDay);
  const nextDeliveryDate = (fromDate) => nextDate(fromDate, isDeliveryDay);
  const formatClock = (cutoff) => `${String(cutoff.hour).padStart(2, '0')}:${String(cutoff.minute).padStart(2, '0')}`;
  const formatDeliveryDate = (date) => new Intl.DateTimeFormat(LOCALE, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC'
  }).format(date).replace(/\./g, '');

  function formatWeekday(date) {
    const value = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', timeZone: 'UTC' }).format(date);
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function getPromise(date = new Date()) {
    if (config.enabled === false) {
      const inStock = STOCK_I18N.inStock || 'In stock';
      return {
        enabled: false,
        shippingToday: false,
        shipDate: null,
        deliveryDate: null,
        deliveryEndDate: null,
        label: '',
        shipCopy: inStock,
        shipShort: inStock,
        secondsToCutoff: 0
      };
    }

    const now = zonedNow(date);
    const today = utcDate(now.year, now.month, now.day);
    const todayRule = ruleForDate(today);
    const nowSeconds = now.hour * 3600 + now.minute * 60 + now.second;
    const cutoffSeconds = todayRule.cutoff.hour * 3600 + todayRule.cutoff.minute * 60;
    const shippingToday = todayRule.ship && nowSeconds < cutoffSeconds;
    const shipDate = shippingToday ? today : nextShipDate(today);

    if (!shipDate) {
      const follows = DELIVERY_I18N.shippingDateFollows || 'Dispatch date to follow';
      return {
        enabled: true,
        shippingToday: false,
        shipDate: null,
        deliveryDate: null,
        deliveryEndDate: null,
        label: '',
        shipCopy: follows,
        shipShort: follows,
        secondsToCutoff: 0
      };
    }

    const deliveryDate = nextDeliveryDate(shipDate);
    const deliveryEndDate = deliveryDate?.getUTCDay() === 6 ? nextDeliveryDate(deliveryDate) : null;
    const label = deliveryDate
      ? (deliveryEndDate
        ? `${formatDeliveryDate(deliveryDate)} – ${formatDeliveryDate(deliveryEndDate)}`
        : formatDeliveryDate(deliveryDate))
      : '';

    let shipCopy;
    let shipShort;
    if (shippingToday) {
      const clock = formatClock(todayRule.cutoff);
      shipCopy = interpolate(DELIVERY_I18N.orderedBeforeShippedToday || 'Order before {{ time }} for dispatch today', { time: clock });
      shipShort = DELIVERY_I18N.todayShipped || 'dispatched today';
    } else if (dateKey(shipDate) === dateKey(addDays(today, 1))) {
      shipCopy = DELIVERY_I18N.tomorrowShipped || 'Dispatched tomorrow';
      shipShort = DELIVERY_I18N.tomorrowShippedShort || 'dispatched tomorrow';
    } else {
      const weekday = formatWeekday(shipDate);
      shipCopy = interpolate(DELIVERY_I18N.weekdayShipped || 'Dispatched {{ day }}', { day: weekday });
      shipShort = interpolate(DELIVERY_I18N.weekdayShippedShort || 'dispatched {{ day }}', { day: weekday });
    }

    return {
      enabled: true,
      now,
      today,
      shippingToday,
      shipDate,
      deliveryDate,
      deliveryEndDate,
      label,
      shipCopy,
      shipShort,
      cutoff: todayRule.cutoff,
      secondsToCutoff: shippingToday ? Math.max(0, cutoffSeconds - nowSeconds) : 0
    };
  }

  function syncAll() {
    const promise = getPromise();
    document.dispatchEvent(new CustomEvent('wub:delivery-synced', { detail: promise }));
    return promise;
  }

  window.WUBDelivery = Object.freeze({
    version: VERSION,
    get config() { return config; },
    refreshConfig,
    getPromise,
    isShipDay,
    isDeliveryDay,
    ruleForDate,
    nextShipDate,
    nextDeliveryDate,
    syncAll
  });

  const queueSync = () => window.requestAnimationFrame(syncAll);
  const refreshAndSync = () => {
    refreshConfig();
    queueSync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueSync, { once: true });
  } else {
    queueSync();
  }

  document.addEventListener('cart:updated', () => window.setTimeout(queueSync, 0));
  document.addEventListener('wub:cart-drawer-opened', queueSync);
  document.addEventListener('shopify:section:load', refreshAndSync);
  window.addEventListener('pageshow', queueSync);
  window.setInterval(queueSync, 60000);
})();
